import React from 'react';
import './TreeNode.css';

// Generic interface for tree nodes
interface TreeNodeData {
  id: string;
  name: string;
  description?: string | null;
  children?: TreeNodeData[];
  parent?: TreeNodeData | null;
}

interface TreeNodeProps<T extends TreeNodeData> {
  node: T;
  onSelect: (id: string) => void;
  selectedId: string | null;
  onAddChild?: (id: string) => void;
  onDelete?: (id: string) => void;
  onImportFromPrime?: (id: string) => void;
  isExpanded: (nodeId: string) => boolean;
  onToggleExpand: (id: string) => void;
}

const TreeNode = <T extends TreeNodeData>({
  node,
  onSelect,
  selectedId,
  onAddChild,
  onDelete,
  onImportFromPrime,
  isExpanded,
  onToggleExpand,
}: TreeNodeProps<T>) => {
  const hasChildren = node.children && node.children.length > 0;

  const handleNodeClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSelect(node.id);
  };

  const handleExpandClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    console.log('Expand button clicked for node:', node.id);
    onToggleExpand(node.id);
  };

  const handleAddChildClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onAddChild) {
      console.log('Add child clicked for node:', node.id);
      onAddChild(node.id);
    }
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onDelete) {
      console.log('Delete clicked for node:', node.id);
      onDelete(node.id);
    }
  };

  const handleImportFromPrimeClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onImportFromPrime) {
      console.log('Import from prime clicked for node:', node.id);
      onImportFromPrime(node.id);
    }
  };

  return (
    <div className="tree-node">
      <div className="node-content">
        {hasChildren && (
          <button
            className={`expand-button ${isExpanded(node.id) ? 'expanded' : ''}`}
            onClick={handleExpandClick}
          >
            {isExpanded(node.id) ? '▼' : '▶'}
          </button>
        )}
        <div
          className={`node-label ${selectedId === node.id ? 'selected' : ''}`}
          onClick={handleNodeClick}
          style={{
            cursor: 'pointer',
            pointerEvents: 'auto',
            position: 'relative',
            zIndex: 1000,
          }}
        >
          {node.name}
        </div>
        <div className="node-controls">
          {onAddChild && (
            <span className="control-icon add" onClick={handleAddChildClick} title="Add Child">
              +
            </span>
          )}
          {onImportFromPrime && (
            <span className="control-icon import" onClick={handleImportFromPrimeClick} title="Import from Prime">
              ↓
            </span>
          )}
          {onDelete && (
            <span className="control-icon delete" onClick={handleDeleteClick} title="Delete">
              ×
            </span>
          )}
        </div>
      </div>

      {hasChildren && isExpanded(node.id) && node.children && (
        <div className="node-children">
          {node.children.map((child) => (
            <TreeNode<T>
              key={child.id}
              node={child as T}
              onSelect={onSelect}
              selectedId={selectedId}
              onAddChild={onAddChild}
              onDelete={onDelete}
              onImportFromPrime={onImportFromPrime}
              isExpanded={isExpanded}
              onToggleExpand={onToggleExpand}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default TreeNode;
